package at.theatervogelweide.admin.contract.overview

import at.theatervogelweide.admin.contract.acts.ActListEntry
import at.theatervogelweide.admin.contract.acts.nextOnTicketPage
import at.theatervogelweide.domain.NewsVisibility
import at.theatervogelweide.domain.TicketSchedule
import kotlinx.serialization.Serializable

/** Everything the start screen shows, counted at one point in time. */
@Serializable
data class Overview(
    /** `null` when no act has a ticket sale and a performance still to come. */
    val upcomingAct: UpcomingAct?,
    val news: NewsCounts,
    val acts: ActCounts,
    val people: PeopleCounts,
    val images: ImageCounts = ImageCounts(),
)

/** The act the ticket page shows now or next ([nextOnTicketPage]). */
@Serializable
data class UpcomingAct(
    val act: ActListEntry,
    val hasReservationUrl: Boolean,
    /** The names of its roles nobody plays yet, the cast before the crew. */
    val rolesWithoutPeople: List<String>,
) {
    val ticketSchedule: TicketSchedule get() = checkNotNull(act.ticketSchedule) { "Act ${act.id} has no ticket schedule" }
}

/** The news items by [NewsVisibility]. */
@Serializable
data class NewsCounts(
    val public: Int,
    val planned: Int,
    val expired: Int,
) {
    val total: Int get() = public + planned + expired

    fun count(visibility: NewsVisibility): Int = when (visibility) {
        NewsVisibility.PUBLIC -> public
        NewsVisibility.PLANNED -> planned
        NewsVisibility.EXPIRED -> expired
    }
}

/** All acts, which the public archive lists; the years are `null` without any. */
@Serializable
data class ActCounts(
    val total: Int,
    val firstYear: Int?,
    val lastYear: Int?,
)

/**
 * The images without a description (#105) by where they are: Stücke (title and other images), news and people.
 * Sponsor logos have the sponsor's name instead.
 */
@Serializable
data class ImageCounts(
    val actsWithoutDescription: Int = 0,
    val newsWithoutDescription: Int = 0,
    val peopleWithoutDescription: Int = 0,
) {
    val withoutDescription: Int get() = actsWithoutDescription + newsWithoutDescription + peopleWithoutDescription
}

@Serializable
data class PeopleCounts(
    val total: Int,
    val withoutPhoto: Int,
)
